import type { ReportClubParticipationRowOutput } from '@schemas'

export type ClubReportSortKey = 'name' | 'exhibitors' | 'entries' | 'points'
export type ClubReportSortDirection = 'asc' | 'desc'

const NAME_NORMALIZE_RE = /^[^A-Za-z0-9]+/

const defaultDirection: Record<ClubReportSortKey, ClubReportSortDirection> = {
  name: 'asc',
  exhibitors: 'desc',
  entries: 'desc',
  points: 'desc',
}

export function getDefaultDirection(key: ClubReportSortKey): ClubReportSortDirection {
  return defaultDirection[key]
}

const normalizeName = (value: string) => value.replace(NAME_NORMALIZE_RE, '').toLocaleLowerCase()

function compareNames(a: ReportClubParticipationRowOutput, b: ReportClubParticipationRowOutput) {
  const normalized = normalizeName(a.clubName).localeCompare(normalizeName(b.clubName))
  if (normalized !== 0) return normalized
  const raw = a.clubName.localeCompare(b.clubName)
  if (raw !== 0) return raw
  return (a.clubId ?? '').localeCompare(b.clubId ?? '')
}

function compareNumbers(a: number, b: number, direction: ClubReportSortDirection): number {
  const base = a - b
  return direction === 'asc' ? base : -base
}

export function sortClubRows(
  rows: ReportClubParticipationRowOutput[],
  key: ClubReportSortKey,
  direction: ClubReportSortDirection
): ReportClubParticipationRowOutput[] {
  const sorted = [...rows]
  sorted.sort((a, b) => {
    switch (key) {
      case 'name': {
        const result = compareNames(a, b)
        if (result !== 0) {
          return direction === 'asc' ? result : -result
        }
        return 0
      }
      case 'exhibitors': {
        const diff = compareNumbers(a.exhibitorCount, b.exhibitorCount, direction)
        if (diff !== 0) return diff
        return compareNames(a, b)
      }
      case 'entries': {
        const diff = compareNumbers(a.entryCount, b.entryCount, direction)
        if (diff !== 0) return diff
        return compareNames(a, b)
      }
      case 'points': {
        const diff = compareNumbers(a.totalPoints, b.totalPoints, direction)
        if (diff !== 0) return diff
        const exhibitorDiff = compareNumbers(a.exhibitorCount, b.exhibitorCount, 'desc')
        if (exhibitorDiff !== 0) return exhibitorDiff
        const entryDiff = compareNumbers(a.entryCount, b.entryCount, 'desc')
        if (entryDiff !== 0) return entryDiff
        return compareNames(a, b)
      }
      default:
        return 0
    }
  })
  return sorted
}

export type ClubReportSort = { key: ClubReportSortKey; direction: ClubReportSortDirection }

/** Choosing the sorted column reverses it; choosing another starts at its default direction. */
export function nextClubSort(current: ClubReportSort, key: ClubReportSortKey): ClubReportSort {
  return current.key === key
    ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { key, direction: getDefaultDirection(key) }
}
